import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject, Observable, forkJoin, of } from 'rxjs';
import { catchError, map, take, tap } from 'rxjs/operators';

import OlMap from 'ol/Map';
import OlFeature from 'ol/Feature';
import type { FeatureLike } from 'ol/Feature';
import VectorSource from 'ol/source/Vector';
import Cluster from 'ol/source/Cluster';
import VectorLayer from 'ol/layer/Vector';
import GeoJSON from 'ol/format/GeoJSON';
import { fromLonLat } from 'ol/proj';
import { Style, Stroke, Fill, Circle as CircleStyle, Text as OlText } from 'ol/style';
import { createEmpty, extend as extendExtent, isEmpty as isExtentEmpty } from 'ol/extent';
import { Geometry, Point } from 'ol/geom';
import { unByKey } from 'ol/Observable';
import type { EventsKey } from 'ol/events';
import {
  clusterDistanceForZoom,
  LRS_DEFAULT_CLUSTER_DISTANCE,
} from 'src/app/shared/utils/lrs-asset-symbology';

import {
  AssetClassification,
  AssetFeature,
  AssetGeoJSONGeometry,
  AssetLayer,
  AssetManagementService,
  AssetStatus,
  CreateAssetRequest,
  UpdateAssetRequest,
} from 'src/app/shared/services/asset-management.service';

export type AssetMapMode = 'view' | 'create' | 'edit';

export interface AssetMapContext {
  mode: AssetMapMode;
  assetId: number | null;
  layerId: number | null;
  classification: AssetClassification | null;
  category: string | null;
}

export interface AssetSavePayload {
  name?: string;
  asset_code?: string;
  description?: string;
  classification?: AssetClassification;
  asset_category?: string;
  status?: AssetStatus;
  depth_m?: number | null;
  elevation_m?: number | null;
  attributes?: Record<string, unknown>;
  change_summary?: string;
}

export interface AssetMapLayerBundle {
  source: VectorSource;
  layer: VectorLayer<VectorSource>;
  clusterSource: Cluster;
  clusterLayer: VectorLayer<VectorSource>;
}

const ASSET_FEATURE_FLAG = '__amAssetFeature__';
const ASSET_ID_FLAG = '__amAssetId__';

/**
 * Bridge between the GIS Asset Management module (REST API) and the existing
 * `gisavimvp` OpenLayers map editor.
 *
 * Responsibilities:
 *  - Read asset-management context from the route (`mode`, `assetId`, `layerId`).
 *  - Provide a single OpenLayers VectorSource where remote assets render.
 *  - Convert between OpenLayers features (EPSG:3857) and GeoJSON (EPSG:4326).
 *  - Persist new and updated geometry back to the asset-management service.
 *
 * The bridge is intentionally stateless from the caller's perspective: the gis
 * editor passes its `OlMap` and a target `VectorSource`, and the bridge takes
 * care of the rest.
 */
@Injectable({ providedIn: 'root' })
export class AssetMapBridgeService {
  private readonly geojsonFmt = new GeoJSON();

  /** Latest context derived from the route; null when not active. */
  private readonly _context$ = new BehaviorSubject<AssetMapContext | null>(null);
  readonly context$ = this._context$.asObservable();

  /** Remote assets currently rendered, keyed by asset id. */
  private readonly _assets = new Map<number, AssetFeature>();
  /** Cached layer metadata for quick lookups (id, name, geometry_type, ...). */
  private readonly _layers = new Map<number, AssetLayer>();

  constructor(private readonly api: AssetManagementService) {}

  // ---------- context / route ----------

  /** Read context from query params. Returns null when no asset-management
   *  context is present. */
  activateFromRoute(route: ActivatedRoute): Observable<AssetMapContext | null> {
    return route.queryParams.pipe(
      take(1),
      map((qp) => {
        const hasContext =
          qp['assetId'] != null ||
          qp['layerId'] != null ||
          qp['mode'] === 'create' ||
          qp['mode'] === 'edit';

        if (!hasContext) {
          this._context$.next(null);
          return null;
        }

        const ctx: AssetMapContext = {
          mode:
            qp['mode'] === 'create' || qp['mode'] === 'edit'
              ? (qp['mode'] as AssetMapMode)
              : qp['assetId']
              ? 'edit'
              : 'view',
          assetId: qp['assetId'] ? Number(qp['assetId']) : null,
          layerId: qp['layerId'] ? Number(qp['layerId']) : null,
          classification:
            qp['classification'] === 'underground' ||
            qp['classification'] === 'overground' ||
            qp['classification'] === 'crossover'
              ? (qp['classification'] as AssetClassification)
              : null,
          category: qp['category'] || null,
        };
        this._context$.next(ctx);
        return ctx;
      })
    );
  }

  getContext(): AssetMapContext | null {
    return this._context$.value;
  }

  setContext(patch: Partial<AssetMapContext>): void {
    const prev: AssetMapContext = this._context$.value || {
      mode: 'view',
      assetId: null,
      layerId: null,
      classification: null,
      category: null,
    };
    this._context$.next({ ...prev, ...patch });
  }

  // ---------- layers ----------

  /** Cache layer metadata for use in geometry validation / payload building. */
  primeLayers(): Observable<AssetLayer[]> {
    return this.api.listLayers(false).pipe(
      map((res) => res.data || []),
      tap((layers) => {
        this._layers.clear();
        layers.forEach((l) => this._layers.set(l.id, l));
      })
    );
  }

  getLayer(id: number | null | undefined): AssetLayer | null {
    if (!id) return null;
    return this._layers.get(id) || null;
  }

  listLayersCached(): AssetLayer[] {
    return Array.from(this._layers.values());
  }

  /**
   * Replace the cached layer set with locally-derived layers (e.g. layers
   * sourced from the gisavimvp `layerRegistry`). No API hit. Useful when
   * the bridge is being driven by the embedded map editor's own layers
   * rather than a remote AssetLayer table.
   */
  useLocalLayers(layers: AssetLayer[]): void {
    this._layers.clear();
    for (const l of layers) {
      this._layers.set(l.id, l);
    }
  }

  // ---------- assets ----------

  /**
   * Load assets from the API matching the active context (or all assets
   * within the active layer) and render them into the supplied source.
   *
   * - Existing features owned by the bridge are removed first.
   * - Assets are reprojected from EPSG:4326 → map projection (default 3857).
   */
  loadAssetsInto(
    source: VectorSource,
    opts: {
      layerId?: number | null;
      classification?: AssetClassification | null;
      mapProjection?: string;
      limit?: number;
    } = {}
  ): Observable<{ count: number; features: OlFeature[] }> {
    const mapProjection = opts.mapProjection || 'EPSG:3857';
    const filters: Record<string, unknown> = { limit: opts.limit ?? 500 };
    if (opts.layerId) filters['layerId'] = opts.layerId;
    if (opts.classification) filters['classification'] = opts.classification;

    return this.api.searchAssets(filters as any).pipe(
      map((collection) => {
        // remove previously rendered bridge features
        source
          .getFeatures()
          .filter((f) => f.get(ASSET_FEATURE_FLAG))
          .forEach((f) => source.removeFeature(f));
        this._assets.clear();

        const features: OlFeature[] = [];
        for (const af of collection.features || []) {
          const olFeat = this.assetFeatureToOl(af, mapProjection);
          if (olFeat) {
            features.push(olFeat);
            this._assets.set(af.id, af);
          }
        }
        if (features.length) source.addFeatures(features);
        return { count: features.length, features };
      })
    );
  }

  /** Re-fetch a single asset and replace its OL feature in the source. */
  refreshAssetInto(
    source: VectorSource,
    assetId: number,
    mapProjection = 'EPSG:3857'
  ): Observable<OlFeature | null> {
    return this.api.getAsset(assetId).pipe(
      map((af) => {
        // drop previous representation
        source
          .getFeatures()
          .filter((f) => f.get(ASSET_ID_FLAG) === assetId)
          .forEach((f) => source.removeFeature(f));
        const olFeat = this.assetFeatureToOl(af, mapProjection);
        if (olFeat) {
          source.addFeature(olFeat);
          this._assets.set(af.id, af);
        }
        return olFeat;
      })
    );
  }

  /** Locate an OL feature in the source by asset id. */
  findOlFeatureByAssetId(source: VectorSource, assetId: number): OlFeature | null {
    return (
      source
        .getFeatures()
        .find((f) => f.get(ASSET_ID_FLAG) === assetId) || null
    );
  }

  /** Zoom + center on the named asset, if present. */
  focusAsset(map: OlMap, source: VectorSource, assetId: number): void {
    const feat = this.findOlFeatureByAssetId(source, assetId);
    if (!feat) return;
    const geom = feat.getGeometry();
    if (!geom) return;
    const ext = geom.getExtent();
    if (!ext || isExtentEmpty(ext)) return;
    map.getView().fit(ext, {
      padding: [80, 80, 80, 80],
      duration: 500,
      maxZoom: 19,
    });
  }

  /** Convenience: fit the view to all currently rendered assets in the source. */
  fitAllAssets(map: OlMap, source: VectorSource): void {
    const feats = source.getFeatures().filter((f) => f.get(ASSET_FEATURE_FLAG));
    if (!feats.length) return;
    const extents = feats
      .map((f) => f.getGeometry()?.getExtent())
      .filter(Boolean) as number[][];
    if (!extents.length) return;
    const merged = extents.reduce((acc, e) => {
      return [
        Math.min(acc[0], e[0]),
        Math.min(acc[1], e[1]),
        Math.max(acc[2], e[2]),
        Math.max(acc[3], e[3]),
      ];
    });
    if (merged && !isExtentEmpty(merged)) {
      map.getView().fit(merged as any, {
        padding: [60, 60, 60, 60],
        duration: 400,
        maxZoom: 18,
      });
    }
  }

  // ---------- save / update / delete ----------

  /** Convert an OL feature into a CreateAssetRequest body. */
  buildCreatePayload(
    feature: OlFeature,
    payload: AssetSavePayload,
    layerId: number,
    mapProjection = 'EPSG:3857'
  ): CreateAssetRequest | null {
    const layer = this.getLayer(layerId);
    const geom = this.olFeatureToGeoJSONGeometry(feature, mapProjection);
    if (!geom) return null;
    const classification: AssetClassification =
      payload.classification ||
      (layer?.classification as AssetClassification) ||
      'overground';
    return {
      layer_id: layerId,
      asset_code: payload.asset_code,
      name: payload.name,
      description: payload.description,
      classification,
      asset_category: payload.asset_category || layer?.asset_category,
      status: payload.status,
      geometry: geom,
      srid: 4326,
      depth_m: payload.depth_m ?? null,
      elevation_m: payload.elevation_m ?? null,
      attributes: payload.attributes,
    };
  }

  saveOlFeatureAsAsset(
    feature: OlFeature,
    layerId: number,
    payload: AssetSavePayload,
    mapProjection = 'EPSG:3857'
  ): Observable<AssetFeature> {
    const body = this.buildCreatePayload(feature, payload, layerId, mapProjection);
    if (!body) {
      throw new Error('Invalid geometry: cannot serialize feature.');
    }
    return this.api.createAsset(body).pipe(
      tap((af) => {
        this._assets.set(af.id, af);
        feature.set(ASSET_FEATURE_FLAG, true);
        feature.set(ASSET_ID_FLAG, af.id);
        feature.setId(`am-${af.id}`);
      })
    );
  }

  updateAssetGeometryFromOl(
    assetId: number,
    feature: OlFeature,
    extra: AssetSavePayload = {},
    mapProjection = 'EPSG:3857'
  ): Observable<AssetFeature> {
    const geom = this.olFeatureToGeoJSONGeometry(feature, mapProjection);
    if (!geom) throw new Error('Invalid geometry for update.');
    const body: UpdateAssetRequest = {
      geometry: geom,
      srid: 4326,
      ...(extra.name !== undefined ? { name: extra.name } : {}),
      ...(extra.asset_code !== undefined ? { asset_code: extra.asset_code } : {}),
      ...(extra.description !== undefined ? { description: extra.description } : {}),
      ...(extra.classification !== undefined ? { classification: extra.classification } : {}),
      ...(extra.asset_category !== undefined ? { asset_category: extra.asset_category } : {}),
      ...(extra.status !== undefined ? { status: extra.status } : {}),
      ...(extra.depth_m !== undefined ? { depth_m: extra.depth_m } : {}),
      ...(extra.elevation_m !== undefined ? { elevation_m: extra.elevation_m } : {}),
      ...(extra.attributes !== undefined ? { attributes: extra.attributes } : {}),
      change_summary: extra.change_summary || 'Geometry updated from map editor',
    };
    return this.api.updateAsset(assetId, body).pipe(
      tap((af) => this._assets.set(af.id, af))
    );
  }

  deleteAsset(assetId: number): Observable<{ id: number; is_deleted: boolean }> {
    return this.api.deleteAsset(assetId).pipe(
      tap(() => this._assets.delete(assetId))
    );
  }

  // ---------- conversions ----------

  /** Convert an asset-management AssetFeature (GeoJSON, EPSG:4326) into an
   *  OpenLayers Feature in the supplied map projection. */
  assetFeatureToOl(
    asset: AssetFeature,
    mapProjection = 'EPSG:3857'
  ): OlFeature | null {
    if (!asset || !asset.geometry) return null;
    try {
      const olFeat = this.geojsonFmt.readFeature(asset, {
        dataProjection: 'EPSG:4326',
        featureProjection: mapProjection,
      }) as OlFeature;
      olFeat.setId(`am-${asset.id}`);
      olFeat.set(ASSET_FEATURE_FLAG, true);
      olFeat.set(ASSET_ID_FLAG, asset.id);
      olFeat.set('amProperties', asset.properties);
      olFeat.set('amName', asset.properties?.name || asset.properties?.asset_code || `Asset ${asset.id}`);
      olFeat.set('amClassification', asset.properties?.classification);
      olFeat.set('amStatus', asset.properties?.status);
      const geom = olFeat.getGeometry();
      if (!(geom instanceof Point)) {
        olFeat.setStyle(this.styleForAsset(asset));
      }
      return olFeat;
    } catch (err) {
      console.warn('[AssetMapBridge] failed to read asset feature', err);
      return null;
    }
  }

  /** Convert an OL feature's geometry to a GeoJSON Geometry in EPSG:4326. */
  olFeatureToGeoJSONGeometry(
    feature: OlFeature,
    mapProjection = 'EPSG:3857'
  ): AssetGeoJSONGeometry | null {
    const geom = feature.getGeometry();
    if (!geom) return null;
    try {
      const obj = this.geojsonFmt.writeGeometryObject(geom as Geometry, {
        dataProjection: 'EPSG:4326',
        featureProjection: mapProjection,
        rightHanded: true,
        decimals: 8,
      }) as AssetGeoJSONGeometry;
      return obj;
    } catch (err) {
      console.warn('[AssetMapBridge] failed to serialize geometry', err);
      return null;
    }
  }

  // ---------- styling ----------

  /** Style remote assets distinctively so they don't blend with drawing layer. */
  private styleForAsset(asset: AssetFeature): Style {
    const cls = asset.properties?.classification;
    const status = asset.properties?.status;

    const stroke =
      cls === 'underground' ? '#8e44ad' : cls === 'crossover' ? '#c026d3' : '#1e88e5';
    const fillRgba =
      cls === 'underground'
        ? 'rgba(142, 68, 173, 0.18)'
        : cls === 'crossover'
          ? 'rgba(192, 38, 211, 0.18)'
          : 'rgba(30, 136, 229, 0.18)';
    const dim =
      status === 'inactive' ||
      status === 'decommissioned' ||
      status === 'abandoned';

    return new Style({
      stroke: new Stroke({
        color: dim ? '#9e9e9e' : stroke,
        width: 2.5,
        lineDash: cls === 'underground' ? [6, 4] : cls === 'crossover' ? [14, 4, 4, 4] : undefined,
      }),
      fill: new Fill({ color: fillRgba }),
      image: new CircleStyle({
        radius: 6,
        fill: new Fill({ color: dim ? '#9e9e9e' : stroke }),
        stroke: new Stroke({ color: '#ffffff', width: 2 }),
      }),
      text: new OlText({
        text:
          asset.properties?.name ||
          asset.properties?.asset_code ||
          `#${asset.id}`,
        offsetY: -16,
        font: '11px sans-serif',
        fill: new Fill({ color: '#1a237e' }),
        stroke: new Stroke({ color: '#ffffff', width: 3 }),
        overflow: true,
      }),
    });
  }

  private styleShapeFeature(feature: FeatureLike): Style | Style[] {
    const geom = feature.getGeometry();
    if (!geom || geom instanceof Point) return [];
    const id = feature.get(ASSET_ID_FLAG);
    const cached = typeof id === 'number' ? this._assets.get(id) : null;
    if (cached) return this.styleForAsset(cached);
    return [];
  }

  private styleClusterFeature(feature: FeatureLike, _resolution?: number): Style | Style[] {
    const members = (feature.get('features') as OlFeature[] | undefined) || [];
    if (members.length <= 1) {
      const inner = members[0];
      const id = inner?.get(ASSET_ID_FLAG);
      const cached = typeof id === 'number' ? this._assets.get(id) : null;
      if (cached) return this.styleForAsset(cached);
      return [];
    }
    const size = members.length;
    const cls = String(members[0]?.get('amClassification') || 'overground');
    const color =
      cls === 'underground' ? '#8e44ad' : cls === 'crossover' ? '#c026d3' : '#1e88e5';
    const radius = Math.min(28, 12 + Math.log10(size) * 6);
    return new Style({
      image: new CircleStyle({
        radius,
        fill: new Fill({ color }),
        stroke: new Stroke({ color: '#fff', width: 2 }),
      }),
      text: new OlText({
        text: String(size),
        font: `bold ${size > 99 ? 11 : 12}px sans-serif`,
        fill: new Fill({ color: '#fff' }),
      }),
    });
  }

  // ---------- layer factory ----------

  /**
   * Build a VectorSource plus two layers: shapes (lines/polygons) and
   * clustered points. The caller adds both layers and still loads into `source`.
   */
  createAssetLayer(): AssetMapLayerBundle {
    const source = new VectorSource();
    const layer = new VectorLayer({
      source,
      zIndex: 950,
      properties: { amAssetLayer: true, name: 'Asset Management' },
      updateWhileAnimating: true,
      updateWhileInteracting: true,
      style: (feature) => this.styleShapeFeature(feature),
    });
    const clusterSource = new Cluster({
      distance: LRS_DEFAULT_CLUSTER_DISTANCE,
      minDistance: 16,
      source,
      geometryFunction: (feature) => {
        const geom = feature.getGeometry();
        return geom instanceof Point ? geom : null;
      },
    });
    const clusterLayer = new VectorLayer({
      source: clusterSource,
      zIndex: 951,
      properties: { amAssetClusterLayer: true, name: 'Asset Management points' },
      updateWhileAnimating: true,
      updateWhileInteracting: true,
      style: (feature, resolution) => this.styleClusterFeature(feature, resolution),
    });
    return { source, layer, clusterSource, clusterLayer };
  }

  /** Keep cluster distance in sync with map zoom. Returns an unsubscribe. */
  syncClusterDistance(
    map: OlMap,
    clusterSource: Cluster,
    basePx: number = LRS_DEFAULT_CLUSTER_DISTANCE
  ): () => void {
    let raf = 0;
    const apply = (): void => {
      raf = 0;
      const zoom = map.getView().getZoom() ?? 0;
      const dist = clusterDistanceForZoom(zoom, basePx);
      clusterSource.setDistance(dist);
      clusterSource.setMinDistance(dist > 0 ? Math.min(16, dist) : 0);
    };
    const key: EventsKey = map.getView().on('change:resolution', () => {
      if (raf) return;
      raf = requestAnimationFrame(apply);
    });
    apply();
    return () => {
      if (raf) cancelAnimationFrame(raf);
      unByKey(key);
    };
  }

  /** Unwrap an OL Cluster hit (or a raw AM feature) into member features. */
  featuresFromHit(feature: FeatureLike | OlFeature | null | undefined): OlFeature[] {
    if (!feature) return [];
    const members = feature.get('features') as OlFeature[] | undefined;
    if (Array.isArray(members) && members.length) return members;
    if (this.isBridgeFeature(feature as OlFeature)) return [feature as OlFeature];
    return [];
  }

  /** Fit the view to clustered member geometries. */
  zoomToCluster(map: OlMap, members: OlFeature[]): void {
    if (!members.length) return;
    const merged = createEmpty();
    let any = false;
    for (const m of members) {
      const ext = m.getGeometry()?.getExtent();
      if (!ext || isExtentEmpty(ext)) continue;
      extendExtent(merged, ext);
      any = true;
    }
    if (!any || isExtentEmpty(merged)) return;
    map.getView().fit(merged, {
      padding: [80, 80, 80, 80],
      duration: 400,
      maxZoom: 19,
    });
  }

  // ---------- introspection ----------

  isBridgeFeature(feature: OlFeature | null | undefined): boolean {
    return !!feature && !!feature.get(ASSET_FEATURE_FLAG);
  }

  getAssetIdFromFeature(feature: OlFeature | null | undefined): number | null {
    if (!feature) return null;
    const id = feature.get(ASSET_ID_FLAG);
    return typeof id === 'number' ? id : null;
  }

  getCachedAsset(assetId: number): AssetFeature | null {
    return this._assets.get(assetId) || null;
  }

  /** Center the map on a longitude/latitude pair. */
  centerOnLngLat(map: OlMap, lng: number, lat: number, zoom?: number): void {
    const view = map.getView();
    view.setCenter(fromLonLat([lng, lat]));
    if (zoom != null) view.setZoom(zoom);
  }

  /** Pre-flight: fetch layer + asset together for "edit geometry" handoff. */
  prepareEditContext(
    assetId: number
  ): Observable<{ asset: AssetFeature; layer: AssetLayer | null }> {
    return forkJoin({
      asset: this.api.getAsset(assetId),
      layers:
        this._layers.size > 0
          ? of(this.listLayersCached())
          : this.primeLayers(),
    }).pipe(
      map(({ asset, layers }) => ({
        asset,
        layer:
          (layers as AssetLayer[]).find((l) => l.id === asset.properties.layer_id) ||
          null,
      })),
      catchError((err) => {
        console.warn('[AssetMapBridge] prepareEditContext failed', err);
        throw err;
      })
    );
  }
}
